import Vue from "vue";
import {
    ClaimState,
    ClaimResponse,
    FetchState,
    ClaimResultSet,
    ExpenseLinkRequest,
    ClaimLinkRequest,
    LinkedExpenses,
    LinkedExpense,
    ClaimExpenseLinkRemoveRequest,
    LinkedClaims,
    LinkedClaim,
    ClaimsPageRequestSortableColumn,
    SortOrder,
    HPHCOptInRequest
} from "@/shared/types";
import * as claimService from "@/shared/api/claimService";
import * as expenseService from "@/shared/api/expenseService";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: ClaimState = {
    claimPageResultSet: {
        claims: null,
        fetchState: FetchState.Uninitialized,
        page: 1,
        pageSize: 10,
        numberOfPages: 1,
        recordCount: 0,
        sortByColumn: ClaimsPageRequestSortableColumn.Date,
        sortOrder: SortOrder.Descending
    },
    searchResultDictionary: {},
    claimLinkedExpensesDictionary: {},
    expenseLinkedClaimsDictionary: {},
    userHasThirdPartyClaims: false,
    hphcOptIn: false,
    hphcOptInFetchState: FetchState.Uninitialized,
    hasClaims: false,
    hasClaimsFetchState: FetchState.Uninitialized
};


export const useClaimStore = defineStore("claim", {
    state: (): ClaimState => (_.cloneDeep(initialState)),
    getters: {
        claims: (state: ClaimState) => state.claimPageResultSet.claims,
        areClaimsLoaded: state => state.claimPageResultSet.fetchState === FetchState.Loaded,
        currentPage: state => state.claimPageResultSet.page,
        pageCount: state => state.claimPageResultSet.numberOfPages,
        pageRange: state => _.range(state.claimPageResultSet.page - 2, state.claimPageResultSet.page + 3)
            .filter(page => page > 0 && page <= state.claimPageResultSet.numberOfPages),
        searchResults: state => (expenseId: number) => state.searchResultDictionary[expenseId].claims,
        searchResultsFetchState: state => (expenseId: number) => state.searchResultDictionary[expenseId].fetchState,
        searchResultsCurrentPage: state => (expenseId: number) => state.searchResultDictionary[expenseId].page,
        searchResultsPageCount: state => (expenseId: number) => state.searchResultDictionary[expenseId].numberOfPages,
        searchResultsPageRange: state => (expenseId: number) =>
            _.range(state.searchResultDictionary[expenseId].page - 2, state.searchResultDictionary[expenseId].page + 3)
                .filter(page => page > 0 && page <= state.searchResultDictionary[expenseId].numberOfPages),
        hasNonMmsClaims: state => state.userHasThirdPartyClaims,
        linkedExpensesFetchState: state => (claimNumber: string) => state.claimLinkedExpensesDictionary[claimNumber].fetchState,
        linkedExpenses: state => (claimNumber: string) => state.claimLinkedExpensesDictionary[claimNumber].linkedExpenses,
        linkedClaimsFetchState: state => (expenseId: number) => state.expenseLinkedClaimsDictionary[expenseId].fetchState,
        linkedClaims: state => (expenseId: number) => state.expenseLinkedClaimsDictionary[expenseId].linkedClaims,
        isHphcOptInLoaded: state => state.hphcOptInFetchState === FetchState.Loaded,
        isHasClaimsLoaded: state => state.hasClaimsFetchState === FetchState.Loaded
    },
    actions: {
        setClaimData (resp) {
            this.claimPageResultSet.claims = resp.records;
            this.claimPageResultSet.page = resp.page;
            this.claimPageResultSet.numberOfPages = resp.numberOfPages;
        },
        setFetchState (newFetchState: FetchState) {
            this.claimPageResultSet.fetchState = newFetchState;
        },
        setPageNumber (page: number): void {
            if (this.claimPageResultSet.page !== page) {
                this.claimPageResultSet.fetchState = FetchState.Uninitialized;
            }
            this.claimPageResultSet.page = page;
        },
        setSearchResultData (args: { resp: any, expenseId: number }) {
            const result = this.searchResultDictionary[args.expenseId];
            result.claims = args.resp.records;
            result.page = args.resp.page;
            result.numberOfPages = args.resp.numberOfPages;
        },
        setSearchResultFetchState (args: { newState: FetchState, expenseId: number }) {
            const result = this.searchResultDictionary[args.expenseId];
            result.fetchState = args.newState;
        },
        setSearchResultPageNumber (args: { page: number, expenseId: number }): void {
            this.searchResultDictionary[args.expenseId].page = args.page;
        },
        setUserThirdPartyClaimFlag (thirdPartyClaimFlag: boolean): void {
            this.userHasThirdPartyClaims = thirdPartyClaimFlag;
        },
        initSearchResult (expenseId: number) {
            const result = this.searchResultDictionary[expenseId];
            if (!result) {
                const entry: ClaimResultSet = {
                    claims: null,
                    fetchState: FetchState.Uninitialized,
                    page: 1,
                    pageSize: 5,
                    numberOfPages: null,
                    recordCount: null,
                    sortByColumn: ClaimsPageRequestSortableColumn.Date,
                    sortOrder: SortOrder.Descending
                };
                Vue.set(this.searchResultDictionary, expenseId, entry);
            }
        },
        initLinkedExpenses (claimNumber: string) {
            const result = this.claimLinkedExpensesDictionary[claimNumber];
            if (!result) {
                const entry: LinkedExpenses = {
                    linkedExpenses: null,
                    fetchState: FetchState.Uninitialized
                };
                Vue.set(this.claimLinkedExpensesDictionary, claimNumber, entry);
            }
        },
        setLinkedExpenses (args: { linkedExpenses: LinkedExpense[], claimNumber: string }) {
            const result = this.claimLinkedExpensesDictionary[args.claimNumber];
            result.linkedExpenses = args.linkedExpenses;
        },
        setLinkedExpensesFetchState (args: { newState: FetchState, claimNumber: string }) {
            const result = this.claimLinkedExpensesDictionary[args.claimNumber];
            result.fetchState = args.newState;
        },
        initLinkedClaims (expenseId: number) {
            const result = this.expenseLinkedClaimsDictionary[expenseId];
            if (!result) {
                const entry: LinkedClaims = {
                    linkedClaims: null,
                    fetchState: FetchState.Uninitialized
                };
                Vue.set(this.expenseLinkedClaimsDictionary, expenseId, entry);
            }
        },
        setLinkedClaims (args: { linkedClaims: LinkedClaim[], expenseId: number }) {
            const result = this.expenseLinkedClaimsDictionary[args.expenseId];
            result.linkedClaims = args.linkedClaims;
        },
        setLinkedClaimsFetchState (args: { newState: FetchState, expenseId: number }) {
            const result = this.expenseLinkedClaimsDictionary[args.expenseId];
            result.fetchState = args.newState;
        },
        setHPHCOptIn (value: boolean) {
            this.hphcOptIn = value;
        },
        setHphcOptInFetchState (newFetchState: FetchState) {
            this.hphcOptInFetchState = newFetchState;
        },
        setHasClaims (value: boolean) {
            this.hasClaims = value;
        },
        setHasClaimsFetchState (newFetchState: FetchState) {
            this.hasClaimsFetchState = newFetchState;
        },
        setSortColumn (col: ClaimsPageRequestSortableColumn) {
            if (this.claimPageResultSet.sortByColumn === col) {
                this.reverserSortOrder();
            }
            this.claimPageResultSet.sortByColumn = col;
            this.claimPageResultSet.fetchState = FetchState.Uninitialized;
            this.loadClaims(1);
        },
        setSortOrder (order: SortOrder) {
            if (order === SortOrder.None) {
                order = SortOrder.Descending;
            }
            if (this.claimPageResultSet.sortOrder === order) {
                return;
            }
            this.claimPageResultSet.sortOrder = order;
            this.claimPageResultSet.fetchState = FetchState.Uninitialized;
            this.loadClaims(1);
        },
        reverserSortOrder () {
            switch (this.claimPageResultSet.sortOrder) {
                case SortOrder.None:
                    this.setSortOrder(SortOrder.Descending);
                    break;
                case SortOrder.Ascending:
                    this.setSortOrder(SortOrder.Descending);
                    break;
                case SortOrder.Descending:
                    this.setSortOrder(SortOrder.Ascending);
                    break;
            }
        },
        async loadClaims (params: { pageNumber: number }): Promise<any> {
            if (this.claimPageResultSet.fetchState === FetchState.Loading) return Promise.resolve();

            try {
                this.setFetchState(FetchState.Loading);
                const axiosResponse = await claimService.getClaims(params.pageNumber, 10, null, null, null, this.claimPageResultSet.sortByColumn, this.claimPageResultSet.sortOrder);
                const resp = (<ClaimResponse>axiosResponse.data);
                const expenseIds = resp.records.map(t => t.linkedExpenseIds);
                const flattenedIds = expenseIds.map(t => t[0]).filter(t => t !== undefined);
                const axiosExpenseServiceResponse = await expenseService.getHsabExpenseStatusesByIds(flattenedIds);
                const statusByExpenseId = axiosExpenseServiceResponse.data;
                resp.records.forEach( record => {
                    const foundStatus = statusByExpenseId.find(p => p.expenseId === record.linkedExpenseIds[0]);
                    if (foundStatus) {
                        record.status = foundStatus.status;
                    } else {
                        record.status = "unsubmitted";
                    }
                });
                this.setClaimData(resp);
                this.setFetchState(FetchState.Loaded);
            } catch (err) {
                return storeHelper.handleError({ error: err, rethrow: false });
            }
        },
        async searchClaims (params: {
            expenseId: number,
            pageNumber: number,
            pageSize: number,
            serviceDate: string,
            providerName: string,
            sortByColumn: ClaimsPageRequestSortableColumn,
            sortOrder: SortOrder
        }): Promise<any> {

            const result = this.searchResultDictionary[params.expenseId];
            if (result && result.fetchState === FetchState.Loading) return Promise.resolve();

            try {
                this.setSearchResultFetchState({ newState: FetchState.Loading, expenseId: params.expenseId });
                const axiosResponse = await claimService.getClaims(
                    params.pageNumber,
                    params.pageSize,
                    params.serviceDate,
                    params.providerName,
                    params.expenseId,
                    params.sortByColumn,
                    params.sortOrder
                );
                const response = (<ClaimResponse>axiosResponse.data);
                this.setSearchResultData({ resp: response, expenseId: params.expenseId });
                this.setSearchResultFetchState({ newState: FetchState.Loaded, expenseId: params.expenseId });
            } catch (err) {
                return storeHelper.handleError({ error: err, rethrow: false });
            }
        },
        linkClaimsToExpense (request: ExpenseLinkRequest): Promise<any> {
            return claimService.linkExpenseToClaims(request).then(() => {
                const notifierStore = useNotifierStore();
                notifierStore.setSuccess("Your expense and medical claim(s) linked successfully.", notifierStore.mediumTimeout);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        linkExpensesToClaim (request: ClaimLinkRequest): Promise<any> {
            return claimService.linkClaimToExpenses(request).then(() => {
                const notifierStore = useNotifierStore();
                notifierStore.setSuccess("Your expense and medical claim(s) linked successfully.", notifierStore.mediumTimeout);
                return this.refreshLinkedExpenses(request.claimNumber);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        loadLinkedExpenses (claimNumber: string): Promise<any> {
            const linkedExpense = this.claimLinkedExpensesDictionary[claimNumber];
            if (linkedExpense.fetchState === FetchState.Loaded) {
                return Promise.resolve();
            }

            this.setLinkedExpensesFetchState({ newState: FetchState.Loading, claimNumber });
            return claimService.getLinkedExpenses(claimNumber).then(response => {
                const linkedExpenses = response.data;
                if (linkedExpenses.length === 0) {
                    this.setLinkedExpenses({ linkedExpenses, claimNumber });
                    this.setLinkedExpensesFetchState({ newState: FetchState.Loaded, claimNumber });
                    return Promise.resolve();
                }
                return expenseService.getExpensesByIds(linkedExpenses.map(e => e.expenseId)).then(expenseResponse => {
                    linkedExpenses.forEach(linkedExpense => {
                        linkedExpense.expense = expenseResponse.data.find(e => e.id === linkedExpense.expenseId);
                    });
                    this.setLinkedExpenses({ linkedExpenses, claimNumber });
                    this.setLinkedExpensesFetchState({ newState: FetchState.Loaded, claimNumber });
                });
            });
        },
        refreshLinkedExpenses (claimNumber: string): Promise<any> {
            this.setLinkedExpensesFetchState({ newState: FetchState.Loading, claimNumber });
            return claimService.getLinkedExpenses(claimNumber).then(response => {
                const linkedExpenses = response.data;
                if (linkedExpenses.length === 0) {
                    this.setLinkedExpenses({ linkedExpenses, claimNumber });
                    this.setLinkedExpensesFetchState({ newState: FetchState.Loaded, claimNumber });
                    return Promise.resolve();
                }
                return expenseService.getExpensesByIds(linkedExpenses.map(e => e.expenseId)).then(expenseResponse => {
                    linkedExpenses.forEach(linkedExpense => {
                        linkedExpense.expense = expenseResponse.data.find(e => e.id === linkedExpense.expenseId);
                    });
                    this.setLinkedExpenses({ linkedExpenses, claimNumber });
                    this.setLinkedExpensesFetchState({ newState: FetchState.Loaded, claimNumber });
                });
            });
        },
        loadLinkedHsabExpenses (claimNumber: string): Promise<any> {
            this.initLinkedExpenses(claimNumber);
            const linkedExpense = this.claimLinkedExpensesDictionary[claimNumber];
            if (linkedExpense.fetchState === FetchState.Loaded) {
                return Promise.resolve();
            }

            this.setLinkedExpensesFetchState({ newState: FetchState.Loading, claimNumber });
            return claimService.getLinkedExpenses(claimNumber).then(response => {
                const linkedExpenses = response.data;
                if (linkedExpenses.length === 0) {
                    this.setLinkedExpenses({ linkedExpenses, claimNumber });
                    this.setLinkedExpensesFetchState({ newState: FetchState.Loaded, claimNumber });
                    return Promise.resolve();
                }
                return expenseService.getHsabExpensesByIds(linkedExpenses.map(e => e.expenseId)).then(expenseResponse => {
                    linkedExpenses.forEach(linkedExpense => {
                        linkedExpense.linkedHsabExpense = expenseResponse.data.find(e => e.id === linkedExpense.expenseId);
                    });
                    this.setLinkedExpenses({ linkedExpenses, claimNumber });
                    this.setLinkedExpensesFetchState({ newState: FetchState.Loaded, claimNumber });
                });
            });
        },
        refreshLinkedHsabExpenses (claimNumber: string): Promise<any> {
            this.setLinkedExpensesFetchState({ newState: FetchState.Loading, claimNumber });
            return claimService.getLinkedExpenses(claimNumber).then(response => {
                const linkedExpenses = response.data;
                if (linkedExpenses.length === 0) {
                    this.setLinkedExpenses({ linkedExpenses, claimNumber });
                    this.setLinkedExpensesFetchState({ newState: FetchState.Loaded, claimNumber });
                    return Promise.resolve();
                }
                return expenseService.getHsabExpensesByIds(linkedExpenses.map(e => e.expenseId)).then(expenseResponse => {
                    linkedExpenses.forEach(linkedExpense => {
                        linkedExpense.linkedHsabExpense = expenseResponse.data.find(e => e.id === linkedExpense.expenseId);
                    });
                    this.setLinkedExpenses({ linkedExpenses, claimNumber });
                    this.setLinkedExpensesFetchState({ newState: FetchState.Loaded, claimNumber });
                });
            });
        },
        loadLinkedClaims (expenseId: number) {
            this.setLinkedClaimsFetchState({ newState: FetchState.Loading, expenseId });
            claimService.getClaimLinksByExpense(expenseId).then(response => {
                const linkedClaims = response.data;
                if (linkedClaims.length === 0) {
                    this.setLinkedClaims({ linkedClaims, expenseId });
                    this.setLinkedClaimsFetchState({ newState: FetchState.Loaded, expenseId });
                    return;
                }
                claimService.getLinkedClaims(expenseId).then(claimResponse => {
                    linkedClaims.forEach(linkedClaim => {
                        linkedClaim.claim = claimResponse.data.find(e => e.claimNumber === linkedClaim.claimNumber);
                    });
                    this.setLinkedClaims({ linkedClaims, expenseId });
                    this.setLinkedClaimsFetchState({ newState: FetchState.Loaded, expenseId });
                });
            });
        },
        initializeSearchResultState (expenseId: number): void {
            this.initSearchResult(expenseId);
        },
        initializeLinkedExpensesState (claimNumber: string): void {
            this.initLinkedExpenses(claimNumber);
        },
        initializeLinkedClaimsState (expenseId: number): void {
            this.initLinkedClaims(expenseId);
        },
        removeClaimToExpenseLink (request: ClaimExpenseLinkRemoveRequest) {
            return claimService.removeClaimExpenseLink(request).then(() => {
                return this.refreshLinkedExpenses(request.claimNumber);
            });
        },
        removeExpenseToClaimLink (request: ClaimExpenseLinkRemoveRequest) {
            return claimService.removeClaimExpenseLink(request).then(() => {
                return this.loadLinkedClaims(request.expenseId);
            });
        },
        removeClaimToHsabExpenseLink (request: ClaimExpenseLinkRemoveRequest) {
            return claimService.removeClaimExpenseLink(request).then(() => {
                return this.refreshLinkedHsabExpenses(request.claimNumber);
            });
        },
        linkHsabExpensesToClaim (request: ClaimLinkRequest): Promise<any> {
            return claimService.linkClaimToExpenses(request)
            .then(response => {
                if (response.data) {
                  this.loadClaims({ pageNumber: this.currentPage })
                  .catch(() => null);
                }
              })
            .then(() => {
                const notifierStore = useNotifierStore();
                notifierStore.setSuccess("Your expense and medical claim(s) linked successfully.", notifierStore.mediumTimeout);
                return this.refreshLinkedHsabExpenses(request.claimNumber);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        removeLinksToExpense (expenseId: number): Promise<any> {
            return claimService.removeLinksToExpense(expenseId)
              .then(response => {
                if (response.data) {
                  this.loadClaims({ pageNumber: this.currentPage })
                  .catch(() => null);
              }
            });
        },
        async loadUserHasThirdPartyClaims (): Promise<any> {
            return claimService.userHasThirdPartyClaims().then(response => {
                this.setUserThirdPartyClaimFlag(response.data);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: false });
            });
        },
        optInHPHC (r: HPHCOptInRequest): Promise<any> {
            return claimService.optInHPHC(r)
                .then(r => this.setHPHCOptIn(r.data))
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        loadIsHPHCOptedIn (): Promise<void> {
            this.setHphcOptInFetchState(FetchState.Loading);
            return claimService.isOptedIn()
                .then(r => {
                    this.setHPHCOptIn(r.data);
                    this.setHphcOptInFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        optOutHPHC (): Promise<boolean> {
            return claimService.optOutHPHC()
                .then(r => {
                    if (r.data) {
                        this.setHPHCOptIn(false);
                    }
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        loadHasClaims (): Promise<void> {
            this.setHasClaimsFetchState(FetchState.Loading);
            return claimService.hasClaims()
                .then(r => {
                    this.setHasClaims(r.data);
                    this.setHasClaimsFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        }
    }
});